/**
 * External dependencies
 */
import { useMemo, useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import useRouter from '@naruto/naruto-hooks/src/useRouter';

/**
 * Internal dependencies
 */
import usePageKey from '@naruto/bgdcms/src/hooks/usePageKey';
import useValidRoute from '@naruto/bgdcms/src/hooks/useValidRoute';
import useReduxApi from '@naruto/bgdcms/src/hooks/useReduxApi';
import useMappedState from '@naruto/bgdcms/src/hooks/useMappedState';
import BaseRoutes from '@naruto/bgdcms/src/base/routes';
import { RouteItem } from './types';

interface UseData {
  /* Data : General */
  mainRoutes: RouteItem[];
  userIsLogin: boolean;
  /* Functions: General */
  gotoHome: () => void;
  onChangeRoute: (route: string) => void;
  logout: () => void;
}

function useData(): UseData {
  /* Global & Local State */
  const { admin } = usePageKey();
  const { push } = useRouter();
  const reduxGlobal = useReduxApi('global');
  const reduxUser = useReduxApi('user');
  const storeUser = useMappedState(state => state.user);
  const validRoutes = useValidRoute([]);

  /* Data */
  const mainRoutes: RouteItem[] = useMemo(() => {
    let config: RouteItem[] = [];
    if (storeUser.userIsLogin) {
      const filterValidRoute = (route: RouteItem): RouteItem | undefined => {
        if (!route.permissionsModule) {
          route.subRoute = route.subRoute ? route.subRoute.filter(filterValidRoute) : [];
          return route;
        }
        return undefined;
      };
      config = cloneDeep(BaseRoutes).Admin.main.filter(filterValidRoute);
    } else {
      config = cloneDeep(BaseRoutes).Guest.main;
    }
    return config;
  }, [storeUser.userIsLogin]);

  /* Functions */
  const onChangeRoute = useCallback((route: string): void => {
    push(route);
    reduxGlobal('postGlobalSideBar', [false]);
  }, []);
  const gotoHome = useCallback((): void => {
    push(validRoutes.redirect);
  }, [validRoutes, admin]);
  const logout = useCallback((): void => {
    reduxUser('postUserLogout', []);
  }, []);

  /* Main */
  return {
    /* Data : General */
    mainRoutes,
    userIsLogin: storeUser.userIsLogin,
    /* Functions : General */
    gotoHome,
    onChangeRoute,
    logout
  };
}

export default useData;
